import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { USER_PORTAL_URL, CLIENT_PORTAL_URL } from '../config/portals';
import { navLoginButtonClassName } from '../constants/homepageUi';

/** Desktop: white / ~90% default; hover full-contrast brand color (no underline). */
const navLinkClass =
  'inline-flex text-sm font-medium text-white/90 transition-colors duration-200 ease-out hover:text-[#00e3ff]';

const Navigation: React.FC = () => {
  const location = useLocation();
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isTrainingDataActive = location.pathname === '/' && location.hash === '#training-data';
  const isContributorsPage =
    location.pathname === '/for-contributors' || location.pathname === '/for-users';
  const navLinkClassFor = (active: boolean) => `${navLinkClass}${active ? ' text-[#00e3ff]' : ''}`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLoginDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="sticky top-0 z-50 border-b border-[rgba(255,255,255,0.05)] bg-[rgba(10,15,20,0.85)] backdrop-blur-[10px] shadow-none">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <img src="/pix-logo.png" alt="Pixano Logo" className="h-12 w-12 object-contain" />
            <span className="text-2xl font-bold text-white">Pixano</span>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <a href="/" className={navLinkClassFor(location.pathname === '/' && !isTrainingDataActive)}>
              Home
            </a>
            <a href="/#training-data" className={navLinkClassFor(isTrainingDataActive)}>
              Training Data
            </a>
            <a href="/for-businesses" className={navLinkClassFor(location.pathname === '/for-businesses')}>
              For Businesses
            </a>
            <a href="/for-contributors" className={navLinkClassFor(isContributorsPage)}>
              For Contributors
            </a>
            <a href="/request-dataset" className={navLinkClassFor(location.pathname === '/request-dataset')}>
              Request Dataset
            </a>
          </div>

          <div className="hidden md:block relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
              className={navLoginButtonClassName}
              onMouseEnter={() => setIsLoginDropdownOpen(true)}
            >
              Login
            </button>
            {isLoginDropdownOpen && (
              <div
                className="absolute right-0 mt-2 w-48 rounded-lg border border-white/10 bg-[rgba(14,18,24,0.98)] backdrop-blur-md py-2 shadow-xl"
                onMouseLeave={() => setIsLoginDropdownOpen(false)}
              >
                <a
                  href={`${USER_PORTAL_URL}/login`}
                  className="block px-4 py-2 text-sm text-[#ccc] transition-colors hover:bg-white/[0.06] hover:text-[#00e3ff]"
                >
                  User Login
                </a>
                <a
                  href={`${CLIENT_PORTAL_URL}/login`}
                  className="block px-4 py-2 text-sm text-[#ccc] transition-colors hover:bg-white/[0.06] hover:text-[#00e3ff]"
                >
                  Customer Login
                </a>
              </div>
            )}
          </div>

          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-[#ccc] hover:text-white hover:bg-white/[0.06]"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/[0.06]">
            <div className="flex flex-col space-y-1">
              <a
                href="/"
                className={`rounded px-4 py-2 transition-colors hover:bg-white/[0.06] hover:text-[#00e3ff] ${
                  location.pathname === '/' && !isTrainingDataActive ? 'text-[#00e3ff]' : 'text-white/90'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </a>
              <Link
                to={{ pathname: '/', hash: 'training-data' }}
                className={`rounded px-4 py-2 transition-colors hover:bg-white/[0.06] hover:text-[#00e3ff] ${
                  isTrainingDataActive ? 'text-[#00e3ff]' : 'text-white/90'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Training Data
              </Link>
              <a
                href="/for-businesses"
                className={`rounded px-4 py-2 transition-colors hover:bg-white/[0.06] hover:text-[#00e3ff] ${
                  location.pathname === '/for-businesses' ? 'text-[#00e3ff]' : 'text-white/90'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                For Businesses
              </a>
              <a
                href="/for-contributors"
                className={`rounded px-4 py-2 transition-colors hover:bg-white/[0.06] hover:text-[#00e3ff] ${
                  isContributorsPage ? 'text-[#00e3ff]' : 'text-white/90'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                For Contributors
              </a>
              <a
                href="/request-dataset"
                className={`rounded px-4 py-2 transition-colors hover:bg-white/[0.06] hover:text-[#00e3ff] ${
                  location.pathname === '/request-dataset' ? 'text-[#00e3ff]' : 'text-white/90'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Request Dataset
              </a>
              <div className="px-4 py-2 border-t border-white/[0.08] mt-2 pt-3">
                <p className="text-sm font-semibold text-white mb-2">Login</p>
                <a
                  href={`${USER_PORTAL_URL}/login`}
                  className={`${navLoginButtonClassName} mb-2 flex w-full justify-center`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  User Login
                </a>
                <a
                  href={`${CLIENT_PORTAL_URL}/login`}
                  className={`${navLoginButtonClassName} flex w-full justify-center`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Customer Login
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
